import { Component } from '@theme/component';

/**
 * @typedef {Object} CartDispatchTimerRefs
 * @property {HTMLElement} timerValue - The element displaying the countdown value.
 * @property {HTMLElement} timer - The element displaying the countdown timer.
 */

/**
 * Countdown timer for same-day dispatch cutoff.
 *
 * Flow:
 * 1. Fetches the Section Rendering API to get:
 *    - Server UTC time from the HTTP `Date` response header (always GMT+0).
 *    - Store timezone offset from the section HTML (`'now' | date: "%:z"`).
 * 2. Calculates remaining seconds until cutoff using only server time — no local clock.
 * 3. On success: shows the timer content and starts a 1-second countdown from the server-calculated remaining time.
 * 4. On any failure: hides the timer content.
 *
 * @extends {Component<CartDispatchTimerRefs>}
 */

class CartDispatchTimer extends Component {
  /** @type {number|null} */
  #intervalId = null;

  /** @type {number} */
  #cutoffHour = 14;

  /** @type {number} Seconds remaining until cutoff, counted down each interval tick. */
  #remainingSeconds = 0;

  /** @type {ChildNode|null} */
  #textNode = null;

  /**
   * Set to `true` once the cutoff time is reached so that visibility-change
   * re-syncs are skipped after the timer has naturally expired.
   *
   * @type {boolean}
   */
  #isExpired = false;

  /**
   * Closest `cart-drawer-component` ancestor, used to pause the interval when
   * the drawer is closed and resume it when the drawer opens.
   *
   * @type {Element|null}
   */
  #drawerRef = null;

  connectedCallback() {
    super.connectedCallback();
    this.#cutoffHour = parseInt(this.dataset.cutoffHour || '14', 10);
    this.#drawerRef = this.closest('cart-drawer-component');
    this.#textNode = this.refs.timerValue?.firstChild || null;

    document.addEventListener('visibilitychange', this.#onVisibilityChange);

    if (this.#drawerRef) {
      this.#drawerRef.addEventListener('dialog:open', this.#onDrawerOpen);
      this.#drawerRef.addEventListener('dialog:close', this.#onDrawerClose);

      // Element may have been morphed into an already-open drawer, so dialog:open will not fire again.
      if (this.isDrawerOpen) this.#syncServerTime();
    } else {
      this.#syncServerTime();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearInterval();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);

    if (this.#drawerRef) {
      this.#drawerRef.removeEventListener('dialog:open', this.#onDrawerOpen);
      this.#drawerRef.removeEventListener('dialog:close', this.#onDrawerClose);
      this.#drawerRef = null;
    }
  }

  /**
   * @param {string} text
   */
  #setTextNode(text) {
    if (this.#textNode) {
      this.#textNode.nodeValue = text;
    }
  }

  /**
   * @returns {boolean} Whether the drawer is open
   */
  get isDrawerOpen() {
    return Boolean(this.#drawerRef?.querySelector('dialog')?.open);
  }

  /**
   * Pauses the countdown when the browser tab becomes hidden; re-syncs with
   * the server when the tab becomes visible again so the remaining time is
   * always accurate (the local countdown drifts while the tab is away).
   *
   * Guards:
   * - Skipped entirely once the cutoff has passed (`#isExpired`).
   * - When inside a drawer, skipped if the drawer is currently closed to
   *   avoid unnecessary network requests.
   */
  #onVisibilityChange = () => {
    if (document.hidden) {
      this.#clearInterval();
      return;
    }

    if (this.#isExpired) return;

    if (this.#drawerRef && !this.isDrawerOpen) return;

    this.#syncServerTime();
  };

  #onDrawerOpen = () => {
    if (this.isDrawerOpen) {
      this.#syncServerTime();
    }
  };

  #onDrawerClose = () => {
    this.#clearInterval();
    this.#hideTimer();
  };

  async #syncServerTime() {
    try {
      const response = await fetch(
        '/cart?sections=section-rendering-dispatch-time', {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
          },
        }
      );

      if (!response.ok) {
        this.#hideTimer();
        return;
      }

      // The HTTP `Date` header is always GMT+0 and set by Shopify's servers,
      // so it is never affected by CDN Liquid caching or the user's local clock.
      const dateHeader = response.headers.get('Date');
      if (!dateHeader) {
        this.#hideTimer();
        return;
      }

      const serverUtcMs = new Date(dateHeader).getTime();

      // Parse the store timezone offset from the section HTML.
      const json = await response.json();
      const html = json['section-rendering-dispatch-time'];
      if (!html) {
        this.#hideTimer();
        return;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const el = doc.querySelector('[data-server-time-zone]');
      if (!(el instanceof HTMLElement) || !el.dataset.serverTimeZone) {
        this.#hideTimer();
        return;
      }

      const timezoneOffsetMs = this.#parseTzOffset(el.dataset.serverTimeZone);

      // Current time in the store's local timezone (server UTC shifted by store offset).
      const localMs = serverUtcMs + timezoneOffsetMs;

      // Cutoff moment today in store's local timezone.
      const cutoff = new Date(localMs);
      cutoff.setUTCHours(this.#cutoffHour, 0, 0, 0);

      const remaining = Math.floor((cutoff.getTime() - localMs) / 1000);

      if (remaining <= 0) {
        this.#hideTimer();
        return;
      }

      this.#remainingSeconds = remaining;
      if (this.#intervalId === null && this.#textNode && !document.hidden) {
        this.#show();
        this.#start();
      }
    } catch {
      // Network error or any unexpected failure — hide the timer content.
      this.#hideTimer();
    }
  }

  #start() {
    this.#tick();
    this.#intervalId = window.setInterval(() => this.#tick(), 1000);
    this.#showTimer();
  }

  #tick() {
    if (this.#remainingSeconds <= 0) {
      this.#hide();
      this.#hideTimer();
      return;
    }

    const hours = Math.floor(this.#remainingSeconds / 3600);
    const minutes = Math.floor((this.#remainingSeconds % 3600) / 60);
    const seconds = this.#remainingSeconds % 60;

    /** @param {number} n */
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

    this.#setTextNode(`${hours}:${pad(minutes)}:${pad(seconds)}`);

    this.#remainingSeconds--;
  }

  /**
   * Parses a UTC offset string (e.g. "+05:30", "-08:00", "+00:00") into milliseconds.
   * @param {string} tz
   * @returns {number}
   */
  #parseTzOffset(tz) {
    if (!tz || tz.length < 6) return 0;
    const sign = tz[0] === '-' ? -1 : 1;
    const parts = tz.slice(1).split(':').map(Number);
    const h = parts[0] ?? 0;
    const m = parts[1] ?? 0;
    return sign * ((h * 60 + m) * 60 * 1000);
  }

  #hide() {
    this.#isExpired = true;
    this.#clearInterval();
    this.hidden = true;
  }

  #show() {
    this.hidden = false;
  }

  #hideTimer() {
    if (this.refs.timer) {
      this.refs.timer.hidden = true;
    }
  }

  #showTimer() {
    if (this.refs.timer) {
      this.refs.timer.hidden = false;
    }
  }

  #clearInterval() {
    if (this.#intervalId !== null) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }
}

if (!customElements.get('cart-dispatch-timer')) {
  customElements.define('cart-dispatch-timer', CartDispatchTimer);
}
